<template>
  <NavigationLayout
    :navigationBarStyle="navigationBarStyle"
    :navigationContentStyle="navigationContentStyle"
    :navigationTitle="navigationTitle"
  >
    <div
      class="shop__wrapper"
      :class="localeClass"
      slot="contents"
      v-show="ready"
    >
      <div class="shop__container">
        <section>
          <div class="title">
            <div class="title_left"></div>
            <p>{{ $t('vipPointShopLabelTitle') }}</p>
            <div class="title_right">
              <img
                src="/img/info_gray.png"
                class="info_icon"
                @click="showInfo"
              />
            </div>
          </div>

          <div class="continuation_status_button_wrapper">
            <button class="continuation_status_button" @click="showContinuationStatus">
              <span class="button_text">{{ $t('chargeGradeContinuationStatusTitle') }}</span>
            </button>
          </div>

          <!-- 所持VIPポイント -->
          <div class="vip_point_container">
            <div class="vip_point_info">
              <div class="point_row">
                <img src="/img/vip_point_2x.png" class="vip_point_icon" />
                <div class="point_amount">{{ formatNumber(vipPoint) }}</div>
              </div>
              <div v-if="vipPoint > 0" class="point_expiry">
                {{ getFormatedExpiredAt(expiredAt) }}
              </div>
              <div class="history_link" @click="showHistory">
                {{ $t('vipPointShopButtonUsageHistory') }}
              </div>
            </div>
          </div>

          <!-- アイテム一覧 -->
          <div class="shop_group">
            <div class="shop_list">
              <div
                v-for="item in filteredShopItems"
                :key="item.id"
                class="shop_item"
                @click="canExchange(item) && showItemDetail(item)"
              >
                <div class="shop_item_content">
                  <div class="shop_item_image">
                    <img :src="item.imageURL" :alt="item.name" />
                  </div>
                  <div class="shop_item_info">
                    <div class="shop_item_name">{{ item.name }}</div>
                    <div class="shop_item_description">
                      {{ item.description }}
                    </div>
                  </div>
                </div>
                <PrimaryButton
                  class="shop_item_price"
                  :class="{ 'shop_item_price--disabled': !canExchange(item) }"
                >
                  <img src="/img/vip_point_2x.png" />
                  {{ formatNumber(item.consumeAmount) }}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </section>

        <!-- アイテム詳細モーダル -->
        <div
          v-if="selectedItem"
          class="item_detail_modal"
          @click="closeItemDetail"
        >
          <div class="item_detail_content" @click.stop>
            <div class="item_detail_header">
              <div class="item_detail_title">
                {{ $t('vipPointExchangeLabelTitle') }}
              </div>
            </div>
            <div class="item_detail_body">
              <div class="item_detail_image">
                <img :src="selectedItem.imageURL" :alt="selectedItem.name" />
              </div>
              <div class="item_detail_info">
                <div class="item_detail_name">
                  {{ selectedItem.name }}
                </div>
                <div class="item_detail_point">
                  <img src="/img/vip_point_2x.png" />
                  {{ formatNumber(selectedItem.consumeAmount) }}
                </div>
                <div class="item_detail_my_point">
                  {{
                    $t('vipPointExchangeLabelPosession', {
                      point: formatNumber(vipPoint),
                    })
                  }}
                </div>
              </div>
            </div>
            <div class="item_detail_footer">
              <PrimaryButton
                class="exchange_button"
                :disabled="
                  !canExchange(selectedItem) || isExchangeButtonDisabled
                "
                @click="exchangeItem(selectedItem)"
              >
                {{ $t('vipPointExchangeButtonExchange') }}
              </PrimaryButton>
              <PrimaryButton color="white" @click="closeItemDetail">
                {{ $t('buttonCancel') }}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import NavigationLayout from '@/components/NavigationLayout.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import Formatter from '@/utils/Formatter'
import dayjs from 'dayjs'
import { VipPointShopItem, VipPointShopItems } from '@/models/VipPointShop'
import { api } from '@/api/appGatewayServerApi'
import { checkInMaintenance } from '@/utils/maintenance'
import { getDurationString } from '@/utils/time'
import { getTokenFromCookie } from '@/utils/token'
import * as models from '@/models'
import {
  handleUnauthorized,
  showGenericNetworkError,
} from '@/utils/handle_error'

@Component({
  components: {
    NavigationLayout,
    PrimaryButton,
  },
})
export default class VipPointShop extends Mixins(LanguageQueryHandler) {
  ready: boolean = false
  vipPointInfo: models.ChargeGradeVipPointInfo | null = null
  vipPoint: number = 0
  expiredAt: string = ''
  selectedItem: VipPointShopItem | null = null
  isExchangeButtonDisabled: boolean = false // 交換ボタンの無効状態を管理

  // ショップアイテム
  shopItems: VipPointShopItems = []

  async mounted() {
    try {
      this.ready = true
      // APIからデータを取得
      await this.fetchVipPointShopItems()

      // VIPポイントの取得
      const res = await api().charge_grade_web.vip_point.get()
      if (res && res.chargeGradeVipPoint) {
        this.vipPointInfo = res.chargeGradeVipPoint
        this.vipPoint = res.chargeGradeVipPoint.total
        this.expiredAt = res.chargeGradeVipPoint.expiredAt
      }
    } catch (e) {
      // エラーハンドリング
      // メンテナンスチェック
      if (checkInMaintenance(e)) {
        this.$router.push({ path: '/maintenance' }).catch(() => {
          // Nothing to do
        })
        return
      }
      // セッションチェック
      await handleUnauthorized(e)
    }
  }

  async fetchVipPointShopItems() {
    try {
      // APIからデータを取得
      const response = await api().vip_point_shop.items()
      if (response && response.vipPointShopItems) {
        this.shopItems = response.vipPointShopItems
      }
    } catch (error) {
      showGenericNetworkError()
      throw error
    }
  }

  formatNumber(num: number): string {
    return Formatter.numberWithCommas(num)
  }

  getFormatedExpiredAt(dateString: string) {
    // Date to String
    const date = dayjs(dateString)
      .tz('Asia/Tokyo')
      .format('YYYY/MM/DD HH:mm:ss')
    return this.$t('vipPointShopLabelExpirationDate', {
      duration: getDurationString(date),
    })
  }

  showItemDetail(item: VipPointShopItem): void {
    this.selectedItem = item
  }

  closeItemDetail(): void {
    this.selectedItem = null
  }

  canExchange(item: VipPointShopItem): boolean {
    return this.vipPoint >= item.consumeAmount
  }

  async exchangeItem(item: VipPointShopItem): Promise<void> {
    // ボタンを無効化して多重リクエストを防止
    this.isExchangeButtonDisabled = true

    try {
      // アイテム交換処理
      await api().vip_point_shop.exchange({
        itemId: item.id,
      })

      // 交換完了ページへ遷移
      this.$router
        .push({
          name: 'vip_point_shop_complete',
          query: {
            itemId: item.id.toString(),
            itemName: item.name,
          },
        })
        .catch(() => {
          // Nothing to do
        })
    } catch (error) {
      showGenericNetworkError()
      this.isExchangeButtonDisabled = false
      // 再読み込み
      window.location.reload()
    }

    // 正常終了時は遷移するので、ページが残っている場合のみ1分後にボタンを再有効化
    setTimeout(() => {
      this.isExchangeButtonDisabled = false
    }, 60000) // 1分 = 60000ミリ秒
  }

  showHistory(): void {
    // 利用履歴ページへ遷移
    this.$router
      .push({
        name: 'charge_grade_vip_point_history',
      })
      .catch(() => {
        // Nothing to do
      })
  }

  showInfo(): void {
    // VIPポイントに関する情報ページに遷移する
    window.location.href = 'https://reality.app/notice/page/4tctfra25bfm'
  }

  showContinuationStatus(): void {
    // 継続ステータスページへ遷移
    this.$router
      .push({
        name: 'charge_grade_continuation_status',
      })
      .catch(() => {
        // Nothing to do
      })
  }

  get localeClass(): string {
    return '-' + this.$i18n.locale
  }

  get filteredShopItems(): VipPointShopItems {
    return this.shopItems.filter(
      (item: VipPointShopItem | null | undefined) =>
        item !== null && item !== undefined
    )
  }

  get isWebview(): boolean {
    // トークンがundefinedでない場合はWebView
    return getTokenFromCookie() !== undefined
  }

  get navigationBarStyle(): object {
    // webviewかどうかでスタイルを切り替え
    if (this.isWebview) {
      // ヘッダー画像の非表示スタイル
      return {
        width: '0',
        height: '0',
        padding: '0',
        margin: '0',
        overflow: 'hidden',
      }
    }

    // 通常のスタイル
    return {
      'border-bottom': '1px solid #e8e8e8',
      'top': 'var(--safe-area-inset-top, 0px)',
    }
  }

  get navigationContentStyle(): object {
    // webviewかどうかでスタイルを切り替え
    if (this.isWebview) {
      return {
        'padding-top': '0px',
      }
    }
    return {
      'padding-top': 'calc(44px + var(--safe-area-inset-top, 0px))',
    }
  }

  get navigationTitle(): string | null {
    // webviewかどうかでタイトルを切り替え
    if (this.isWebview) {
      return ''
    }
    return null
  }
}
</script>

<style scoped>
.shop__wrapper {
  position: relative;
  height: auto;
  min-height: 100%;
}

.shop__container {
  font-family: 'Hiragino Sans', sans-serif;
  font-size: 14px;
  line-height: 1.43;
  color: #262626;
  padding: 0 16px 40px 16px;

  @media (min-width: 800px) {
    width: 800px;
    margin: 0 auto;
  }
}

.title {
  width: 100%;
  margin: 32px 0 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  & p {
    font-size: 16px;
    font-weight: 600;
    font-family: 'Hiragino Sans', sans-serif;
    margin-bottom: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  & .title_left {
    width: 20px;
    flex-shrink: 0;
  }

  & .title_right {
    width: 20px;
    flex-shrink: 0;
    position: absolute;
    right: 16px;

    @media (min-width: 800px) {
      right: calc(50% - 400px + 16px);
    }
  }

  & .info_icon {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
}

.continuation_status_button_wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 8px; /* 16pxから8pxに減らす */
  width: 100%;
}

.continuation_status_button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 12px;
  gap: 2px;
  min-width: 140px;
  max-width: 100%;
  height: 24px;
  background: #F2F2F2;
  border-radius: 12px;
  border: none;
  cursor: pointer;
}

.button_text {
  width: auto;
  min-width: 124px;
  height: 16px;
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  letter-spacing: 0.4px;
  color: #0F0F0F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 所持VIPポイント */
.vip_point_container {
  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; 
  padding: 8px 0px;
  gap: 8px;

  width: 100%; 
  height: 86px;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
  margin: 24px auto 24px; 
}

.vip_point_info {
  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; 
  padding: 0px;
  gap: 4px;

  width: auto; 
  height: 70px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
  margin: 0 auto; /* 左右のマージンを自動に設定して中央揃え */
}

.point_row {
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 12px 0px 0px;
  gap: 8px;

  width: auto; 
  height: 30px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
}

.vip_point_icon {
  width: 24px;
  height: 24px;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
}

.point_amount {
  width: auto; 
  height: 30px;

  /* iOS/JP/H2 */
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
  /* identical to box height, or 150% */
  display: flex;
  align-items: center;
  text-align: right;
  letter-spacing: 0.15px;

  color: #0F0F0F;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
}

.point_expiry {
  width: auto; 
  height: 16px;

  /* iOS/JP/Caption */
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  /* identical to box height, or 133% */
  text-align: center; 
  letter-spacing: 0.4px;

  color: #7B7B7B;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
}

.history_link {
  width: auto; 
  height: 16px;

  /* iOS/JP/Caption */
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  /* identical to box height, or 133% */
  text-align: center; 
  letter-spacing: 0.4px;

  color: #188DFF;

  /* Inside auto layout */
  flex: none;
  order: 2;
  flex-grow: 0;
  
  cursor: pointer;
}

.shop_group .shop_list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 16px 16px 0;
}

.shop_group .shop_list .shop_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 12px 12px 8px;
  width: 100%;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: transform 0.2s;
}

@media (min-width: 832px) {
  .shop_group .shop_list .shop_item {
    width: 800px;
  }
}

.shop_group .shop_list .shop_item .shop_item_price {
  -webkit-text-size-adjust: 100%;
  font-weight: 600;
  font-size: 12px;
  height: 24px;
  min-width: 80px;
  width: auto;
  white-space: nowrap;
  padding: 0px 12px;
  margin-left: 8px;
}

.shop_item_price {
  & img {
    vertical-align: sub;
    width: 16px;
    height: 16px;
  }

  &.shop_item_price--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.shop_item_content {
  display: flex;
  align-items: center;
  flex: 1;
}

.shop_item_image {
  width: 64px;
  height: 64px;
  margin-right: 8px;

  & img {
    width: 64px;
    height: 64px;
    object-fit: cover;
  }
}

.shop_item_info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
}

.shop_item_name {
  font-family: 'Hiragino Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.shop_item_description {
  font-size: 12px;
  color: #7b7b7b;
}

/* モーダル */
.item_detail_modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.item_detail_content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
}

.item_detail_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.item_detail_title {
  font-family: 'Hiragino Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  text-align: center;
}

.item_detail_body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.item_detail_image {
  width: 100%;
  max-width: 120px;
  margin: 0 auto;

  & img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.item_detail_info {
  flex: 1;
}

.item_detail_name {
  font-family: 'Hiragino Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 12px;
}

.item_detail_point {
  font-family: 'Hiragino Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  & img {
    width: 24px;
    height: 24px;
    margin-right: 4px;
  }
}

.item_detail_my_point {
  font-family: 'Hiragino Sans', sans-serif;
  font-size: 12px;
  font-weight: 300;
  text-align: center;
}

.item_detail_content_item {
  margin-bottom: 8px;
  padding: 8px;
  background-color: #f8f8f8;
  border-radius: 4px;
}

.item_detail_footer {
  margin-top: 24px;
}

.exchange_button {
  width: 100%;
  margin-bottom: 24px;

  &:disabled {
    cursor: wait; /* 処理中であることを示すカーソルに変更 */
    opacity: 0.7; /* 透明度を下げて視覚的にも処理中であることを示す */
  }
}
</style>
